import {
  fetchAuthSession as amplifyFetchAuthSession,
  getCurrentUser as amplifyGetCurrentUser,
  signInWithRedirect as amplifySignInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import {
  getNativeAuthSession,
  getNativeCurrentUser,
  getNativeUserIdHint,
  isNativeAuthMode,
  nativeSignOut,
  requestNativeSignIn,
} from './nativeBridge';

export { getNativeUserIdHint, isNativeAuthMode, requestNativeSignIn };

export const AUTH_LOST_EVENT = 'thinklet:auth-lost';

let lastAuthLostNotificationAt = 0;

const PERSISTED_TOKEN_SUFFIXES = ['.accessToken', '.idToken', '.refreshToken', '.LastAuthUser'];
const OAUTH_REDIRECT_MARKER_KEY = 'thinklet:oauth_redirect_started_at';
const OAUTH_REDIRECT_GRACE_MS = 15000;
const WEB_AUTH_TIMEOUT_MS = 4000;

const resolveHostedUiDomain = (): string => {
  const configuredDomain = process.env.REACT_APP_COGNITO_DOMAIN || '';
  if (!configuredDomain) {
    return '';
  }

  if (configuredDomain.includes('.')) {
    return configuredDomain;
  }

  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
  const region = userPoolId.split('_')[0];
  if (!region) {
    return configuredDomain;
  }

  return `${configuredDomain}.auth.${region}.amazoncognito.com`;
};

const resolveRedirectUrl = (): string => {
  const configuredRedirectUrl = process.env.REACT_APP_REDIRECT_URL || 'http://localhost:3000';

  if (typeof window === 'undefined') {
    return configuredRedirectUrl;
  }

  const currentOrigin = window.location.origin;
  const isLocalOrigin =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  if (!isLocalOrigin) {
    return currentOrigin;
  }

  return configuredRedirectUrl;
};

export const resolveHostedUiLogoutUrl = (redirectUrl = resolveRedirectUrl()): string | null => {
  const domain = resolveHostedUiDomain();
  const clientId = process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || '';
  if (!domain || !clientId || !redirectUrl) {
    return null;
  }

  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: redirectUrl,
  });
  return `https://${domain}/logout?${params.toString()}`;
};

export const isOAuthRedirectInProgress = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const hasOAuthCallbackParams = params.has('code') && params.has('state');
    if (hasOAuthCallbackParams) {
      window.sessionStorage.setItem(OAUTH_REDIRECT_MARKER_KEY, String(Date.now()));
      return true;
    }

    const redirectStartedAt = Number(window.sessionStorage.getItem(OAUTH_REDIRECT_MARKER_KEY) || '0');
    if (!Number.isFinite(redirectStartedAt) || redirectStartedAt <= 0) {
      return false;
    }

    const withinGracePeriod = Date.now() - redirectStartedAt < OAUTH_REDIRECT_GRACE_MS;
    if (!withinGracePeriod) {
      window.sessionStorage.removeItem(OAUTH_REDIRECT_MARKER_KEY);
    }

    return withinGracePeriod;
  } catch {
    return false;
  }
};

export const hasPersistedWebAuthSession = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return Object.keys(window.localStorage).some(
      (key) =>
        key.startsWith('CognitoIdentityServiceProvider.') &&
        PERSISTED_TOKEN_SUFFIXES.some((suffix) => key.endsWith(suffix)),
    );
  } catch {
    return false;
  }
};

const shouldNotifyAuthLost = (): boolean => !isOAuthRedirectInProgress();

const notifyAuthLost = (reason: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const now = Date.now();
  if (now >= lastAuthLostNotificationAt && now - lastAuthLostNotificationAt < 5000) {
    return;
  }

  lastAuthLostNotificationAt = now;
  window.dispatchEvent(
    new CustomEvent(AUTH_LOST_EVENT, {
      detail: { reason, occurredAt: new Date(now).toISOString() },
    }),
  );
};

const withAuthTimeout = async <T>(operationName: string, operation: Promise<T>): Promise<T> => {
  let timeoutId: number | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(`${operationName} timed out after ${WEB_AUTH_TIMEOUT_MS}ms`));
    }, WEB_AUTH_TIMEOUT_MS);
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
};

export const signInWithGoogleRedirect = async (): Promise<void> => {
  if (isNativeAuthMode()) {
    requestNativeSignIn();
    return;
  }

  await amplifySignInWithRedirect({
    provider: 'Google',
    options: {
      prompt: 'SELECT_ACCOUNT',
    },
  });
};

export const fetchAuthSession = async (...args: any[]): Promise<any> => {
  if (isNativeAuthMode()) {
    const nativeSession = getNativeAuthSession();
    if (!nativeSession?.tokens?.idToken || !nativeSession.tokens.accessToken) {
      throw new Error('No native auth session available');
    }
    return nativeSession;
  }

  try {
    return await withAuthTimeout('fetchAuthSession', (amplifyFetchAuthSession as any)(...args));
  } catch (error) {
    if (shouldNotifyAuthLost()) {
      const reason =
        error instanceof Error && error.message.includes('timed out')
          ? 'fetchAuthSession_timeout'
          : 'fetchAuthSession_failed';
      notifyAuthLost(reason);
    }
    throw error;
  }
};

export const getCurrentUser = async (...args: any[]): Promise<any> => {
  if (isNativeAuthMode()) {
    const nativeUser = getNativeCurrentUser();
    if (!nativeUser) {
      throw new Error('No native current user available');
    }
    return nativeUser;
  }

  try {
    return await withAuthTimeout('getCurrentUser', (amplifyGetCurrentUser as any)(...args));
  } catch (error) {
    if (shouldNotifyAuthLost()) {
      const reason =
        error instanceof Error && error.message.includes('timed out')
          ? 'getCurrentUser_timeout'
          : 'getCurrentUser_failed';
      notifyAuthLost(reason);
    }
    throw error;
  }
};

export const signOut = async (...args: any[]): Promise<void> => {
  if (isNativeAuthMode()) {
    nativeSignOut();
    return;
  }
  await (amplifySignOut as any)(...args);
};

export const signOutWithHostedUiRedirect = async (): Promise<void> => {
  if (isNativeAuthMode()) {
    nativeSignOut();
    return;
  }

  const logoutUrl = resolveHostedUiLogoutUrl();

  try {
    await (amplifySignOut as any)({ global: true });
  } finally {
    if (typeof window === 'undefined') {
      return;
    }

    if (logoutUrl) {
      window.location.assign(logoutUrl);
    } else {
      window.location.reload();
    }
  }
};
